/**
 * ReporteCierre — Cierre financiero diario / mensual.
 *
 * Endpoints:
 *   GET /api/admin/reportes/cierre?from=&to=&groupBy=day|month
 *   GET /api/admin/reportes/cierre/export?format=csv|xlsx&from=&to=&groupBy=
 *
 * Muestra:
 *  - Totales generales (ventas brutas/netas, IVA, devoluciones, ticket,
 *    comisiones estimadas).
 *  - Tabla por periodo.
 *  - Gráfico de barras stacked por método de pago.
 *  - Botones de exportación.
 *
 * NO toca routes.tsx ni Sidebar.tsx — el deployer las integra.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2,
  Filter,
  Download,
  FileSpreadsheet,
  Coins,
  Receipt,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  ArrowDownToLine,
  Calendar,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { apiGet, formatMXN } from '../lib/api'

// ────────────────────────────────────────────────────────────────────
// Tipos respuesta
// ────────────────────────────────────────────────────────────────────

interface MethodBucket {
  gross: number
  commission_est: number
  net: number
  count: number
}

interface CierrePeriod {
  date: string
  totalSales: number
  totalRefunds: number
  netSales: number
  ivaCollected: number
  byMethod: Record<string, MethodBucket>
  ordersCount: number
  refundsCount: number
  avgTicket: number
}

interface CierreTotals {
  totalSales: number
  totalRefunds: number
  netSales: number
  ivaCollected: number
  byMethod: Record<string, MethodBucket>
  ordersCount: number
  refundsCount: number
  avgTicket: number
  commissionTotal: number
}

interface FeeRow {
  method: string
  label: string
  percent: number
  flat: number
}

interface CierreResponse {
  from: string
  to: string
  groupBy: 'day' | 'month'
  periods: CierrePeriod[]
  totals: CierreTotals
  fees: FeeRow[]
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function defaultFrom(): string {
  const d = new Date()
  d.setUTCDate(1)
  return isoDate(d)
}

function defaultTo(): string {
  return isoDate(new Date())
}

const METHOD_COLORS: Record<string, string> = {
  mercadopago: '#3B82F6',
  paypal: '#0070BA',
  aplazo: '#8B5CF6',
  transferencia: '#10B981',
  efectivo: '#F59E0B',
  oxxo: '#EF4444',
  otro: '#6B7280',
}

const METHOD_LABELS: Record<string, string> = {
  mercadopago: 'Mercado Pago',
  paypal: 'PayPal',
  aplazo: 'Aplazo',
  transferencia: 'Transferencia',
  efectivo: 'Efectivo',
  oxxo: 'OXXO',
  otro: 'Otros',
}

const METHOD_ORDER = ['mercadopago', 'paypal', 'aplazo', 'transferencia', 'efectivo', 'oxxo', 'otro']

// ────────────────────────────────────────────────────────────────────
// Página
// ────────────────────────────────────────────────────────────────────

export function ReporteCierre() {
  const [from, setFrom] = useState(defaultFrom())
  const [to, setTo] = useState(defaultTo())
  const [groupBy, setGroupBy] = useState<'day' | 'month'>('day')

  const queryUrl = `/api/admin/reportes/cierre?from=${from}&to=${to}&groupBy=${groupBy}`
  const { data, isLoading, error } = useQuery<CierreResponse>({
    queryKey: ['reportes', 'cierre', from, to, groupBy],
    queryFn: () => apiGet<CierreResponse>(queryUrl),
  })

  function exportUrl(format: 'csv' | 'xlsx'): string {
    return `/api/admin/reportes/cierre/export?format=${format}&from=${from}&to=${to}&groupBy=${groupBy}`
  }

  return (
    <>
      <Topbar
        title="Cierre financiero"
        subtitle="Totales por día o mes con desglose de método de pago, IVA y comisiones"
        actions={
          <>
            <a
              href={exportUrl('csv')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
            >
              <Download className="size-3.5" /> CSV
            </a>
            <a
              href={exportUrl('xlsx')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              <FileSpreadsheet className="size-3.5" /> Excel
            </a>
          </>
        }
      />

      <main className="space-y-6 p-6">
        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-ink-200 bg-white p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-ink-500">
            <Filter className="size-3.5" /> Rango
          </div>
          <label className="flex flex-col text-xs text-ink-600">
            Desde
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col text-xs text-ink-600">
            Hasta
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </label>

          <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 text-sm">
            {(['day', 'month'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                className={`rounded-md px-3 py-1 font-medium ${
                  groupBy === g
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                {g === 'day' ? 'Diario' : 'Mensual'}
              </button>
            ))}
          </div>

          <div className="ml-auto flex flex-wrap gap-1">
            <PresetButton
              label="Hoy"
              onClick={() => {
                const t = isoDate(new Date())
                setFrom(t)
                setTo(t)
                setGroupBy('day')
              }}
            />
            <PresetButton
              label="Ayer"
              onClick={() => {
                const d = new Date()
                d.setUTCDate(d.getUTCDate() - 1)
                const t = isoDate(d)
                setFrom(t)
                setTo(t)
                setGroupBy('day')
              }}
            />
            <PresetButton
              label="Mes actual"
              onClick={() => {
                setFrom(defaultFrom())
                setTo(defaultTo())
                setGroupBy('day')
              }}
            />
            <PresetButton
              label="Mes anterior"
              onClick={() => {
                const d = new Date()
                d.setUTCDate(1)
                d.setUTCMonth(d.getUTCMonth() - 1)
                const f = isoDate(d)
                const last = new Date(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)
                setFrom(f)
                setTo(isoDate(last))
                setGroupBy('day')
              }}
            />
            <PresetButton
              label="Año"
              onClick={() => {
                const d = new Date()
                setFrom(`${d.getUTCFullYear()}-01-01`)
                setTo(isoDate(new Date()))
                setGroupBy('month')
              }}
            />
          </div>
        </div>

        {/* Total general arriba */}
        <StateView isLoading={isLoading} error={error}>
          {data && <TotalsHeader totals={data.totals} />}

          {data && (
            <>
              {/* Stacked bar chart */}
              {data.periods.length > 0 && (
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-ink-900">
                      Ventas brutas por método ({data.groupBy === 'day' ? 'día' : 'mes'})
                    </h3>
                    <Legend methods={visibleMethods(data)} />
                  </div>
                  <StackedBarChart periods={data.periods} />
                </div>
              )}

              {/* Tabla por periodo */}
              <PeriodTable periods={data.periods} groupBy={data.groupBy} />

              {/* Catálogo de comisiones (informativo) */}
              <FeesCatalog fees={data.fees} totals={data.totals} />
            </>
          )}
        </StateView>
      </main>
    </>
  )
}

// ────────────────────────────────────────────────────────────────────
// Subcomponentes
// ────────────────────────────────────────────────────────────────────

function PresetButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
    >
      {label}
    </button>
  )
}

function StateView({
  isLoading,
  error,
  children,
}: {
  isLoading: boolean
  error: unknown
  children: React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-20 text-ink-500">
        <Loader2 className="size-8 animate-spin" />
        <p className="mt-3 text-sm">Generando cierre…</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(error as Error).message}
      </div>
    )
  }
  return <>{children}</>
}

function TotalsHeader({ totals }: { totals: CierreTotals }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <BigStat
        label="Ventas brutas"
        value={formatMXN(totals.totalSales)}
        icon={ShoppingCart}
        tone="blue"
      />
      <BigStat
        label="Devoluciones"
        value={formatMXN(totals.totalRefunds)}
        icon={TrendingDown}
        tone="red"
      />
      <BigStat
        label="Ventas netas"
        value={formatMXN(totals.netSales)}
        icon={TrendingUp}
        tone="emerald"
      />
      <BigStat
        label="IVA recaudado"
        value={formatMXN(totals.ivaCollected)}
        icon={Receipt}
        tone="violet"
      />
      <BigStat
        label="Comisión est."
        value={formatMXN(totals.commissionTotal)}
        icon={Coins}
        tone="amber"
      />
      <BigStat
        label="Ticket promedio"
        value={formatMXN(totals.avgTicket)}
        icon={ArrowDownToLine}
        tone="indigo"
        sub={`${totals.ordersCount.toLocaleString('es-MX')} pedidos · ${totals.refundsCount} dev.`}
      />
    </div>
  )
}

type Tone = 'blue' | 'red' | 'emerald' | 'violet' | 'amber' | 'indigo'

function BigStat({
  label,
  value,
  icon: Icon,
  tone,
  sub,
}: {
  label: string
  value: string
  icon: typeof ShoppingCart
  tone: Tone
  sub?: string
}) {
  const palette: Record<Tone, { bg: string; text: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-700' },
    red: { bg: 'bg-red-50', text: 'text-red-700' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-700' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  }
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-medium text-ink-500">{label}</div>
          <div className="mt-1 truncate text-xl font-extrabold text-ink-900">
            {value}
          </div>
          {sub && <div className="mt-0.5 text-[11px] text-ink-500">{sub}</div>}
        </div>
        <div className={`grid size-9 shrink-0 place-items-center rounded-xl ${palette[tone].bg}`}>
          <Icon className={`size-4 ${palette[tone].text}`} />
        </div>
      </div>
    </div>
  )
}

function visibleMethods(data: CierreResponse): string[] {
  const used = new Set<string>()
  for (const m of Object.keys(data.totals.byMethod)) {
    const b = data.totals.byMethod[m]
    if (b && (b.gross > 0 || b.count > 0)) used.add(m)
  }
  return METHOD_ORDER.filter((m) => used.has(m))
}

function Legend({ methods }: { methods: string[] }) {
  if (methods.length === 0) return null
  return (
    <ul className="flex flex-wrap items-center gap-3 text-xs text-ink-600">
      {methods.map((m) => (
        <li key={m} className="flex items-center gap-1.5">
          <span
            className="inline-block size-2.5 rounded-full"
            style={{ background: METHOD_COLORS[m] ?? METHOD_COLORS.otro }}
          />
          {METHOD_LABELS[m] ?? m}
        </li>
      ))}
    </ul>
  )
}

// ────────────────────────────────────────────────────────────────────
// Stacked Bar SVG
// ────────────────────────────────────────────────────────────────────

function StackedBarChart({ periods }: { periods: CierrePeriod[] }) {
  const data = periods
  const W = 800
  const H = 280
  const PAD_L = 60
  const PAD_B = 40
  const PAD_T = 10
  const PAD_R = 20

  const maxTotal = useMemo(() => {
    let m = 0
    for (const p of data) {
      if (p.totalSales > m) m = p.totalSales
    }
    return m || 1
  }, [data])

  if (data.length === 0) {
    return (
      <p className="grid place-items-center py-10 text-sm text-ink-500">
        Sin pedidos en el rango seleccionado.
      </p>
    )
  }

  const chartW = W - PAD_L - PAD_R
  const chartH = H - PAD_T - PAD_B
  const barGap = 8
  const barW = Math.max(8, Math.min(56, chartW / data.length - barGap))
  const yScale = (v: number) => (v / maxTotal) * chartH

  // Eje Y: 4 ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    y: PAD_T + chartH - t * chartH,
    label: formatMXN(t * maxTotal),
  }))

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[600px]" preserveAspectRatio="xMidYMid meet">
        {/* Grid */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={t.y}
              y2={t.y}
              stroke="#E5E7EB"
              strokeDasharray="2 3"
            />
            <text
              x={PAD_L - 6}
              y={t.y + 3}
              fontSize={10}
              fill="#6B7280"
              textAnchor="end"
            >
              {t.label}
            </text>
          </g>
        ))}

        {/* Bars */}
        {data.map((p, i) => {
          const x = PAD_L + i * (barW + barGap) + barGap / 2
          let stackY = PAD_T + chartH
          return (
            <g key={p.date}>
              {METHOD_ORDER.map((m) => {
                const b = p.byMethod[m]
                if (!b || b.gross <= 0) return null
                const h = yScale(b.gross)
                stackY -= h
                return (
                  <rect
                    key={m}
                    x={x}
                    y={stackY}
                    width={barW}
                    height={h}
                    fill={METHOD_COLORS[m] ?? METHOD_COLORS.otro}
                    opacity={0.92}
                  >
                    <title>{`${METHOD_LABELS[m] ?? m}: ${formatMXN(b.gross)}`}</title>
                  </rect>
                )
              })}
              <text
                x={x + barW / 2}
                y={H - PAD_B + 14}
                fontSize={9}
                fill="#6B7280"
                textAnchor="middle"
              >
                {formatXLabel(p.date)}
              </text>
            </g>
          )
        })}

        {/* Eje X baseline */}
        <line
          x1={PAD_L}
          x2={W - PAD_R}
          y1={PAD_T + chartH}
          y2={PAD_T + chartH}
          stroke="#9CA3AF"
        />
      </svg>
    </div>
  )
}

function formatXLabel(date: string): string {
  // YYYY-MM-DD → "DD/MM"  · YYYY-MM → "MM/YY"
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [, m, d] = date.split('-')
    return `${d}/${m}`
  }
  if (/^\d{4}-\d{2}$/.test(date)) {
    const [y, m] = date.split('-')
    return `${m || ''}/${y?.slice(2) || ''}`
  }
  return date
}

// ────────────────────────────────────────────────────────────────────
// Tabla por periodo
// ────────────────────────────────────────────────────────────────────

function PeriodTable({
  periods,
  groupBy,
}: {
  periods: CierrePeriod[]
  groupBy: 'day' | 'month'
}) {
  if (periods.length === 0) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-10 text-center text-sm text-ink-500">
        <Calendar className="mx-auto mb-2 size-5 text-ink-400" />
        Sin movimientos en este rango.
      </div>
    )
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-ink-50">
          <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
            <th className="px-4 py-3 font-medium">
              {groupBy === 'day' ? 'Día' : 'Mes'}
            </th>
            <th className="px-4 py-3 text-right font-medium">Ventas brutas</th>
            <th className="px-4 py-3 text-right font-medium">Devoluciones</th>
            <th className="px-4 py-3 text-right font-medium">Ventas netas</th>
            <th className="px-4 py-3 text-right font-medium">IVA</th>
            <th className="px-4 py-3 text-right font-medium"># Pedidos</th>
            <th className="px-4 py-3 text-right font-medium">Ticket prom.</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p.date} className="border-t border-ink-100 hover:bg-ink-50">
              <td className="px-4 py-3 font-mono text-xs text-ink-900">{p.date}</td>
              <td className="px-4 py-3 text-right font-semibold text-ink-900">
                {formatMXN(p.totalSales)}
              </td>
              <td className="px-4 py-3 text-right text-red-700">
                {p.totalRefunds > 0 ? `- ${formatMXN(p.totalRefunds)}` : '—'}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                {formatMXN(p.netSales)}
              </td>
              <td className="px-4 py-3 text-right text-violet-700">
                {formatMXN(p.ivaCollected)}
              </td>
              <td className="px-4 py-3 text-right text-ink-700">
                {p.ordersCount.toLocaleString('es-MX')}
              </td>
              <td className="px-4 py-3 text-right text-ink-700">
                {formatMXN(p.avgTicket)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Catálogo de comisiones (informativo)
// ────────────────────────────────────────────────────────────────────

function FeesCatalog({ fees, totals }: { fees: FeeRow[]; totals: CierreTotals }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white">
      <div className="border-b border-ink-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-ink-900">Detalle por método de pago</h3>
        <p className="mt-0.5 text-xs text-ink-500">
          Comisiones estimadas según tasas configuradas en
          <code className="ml-1 rounded bg-ink-100 px-1 py-0.5 font-mono text-[11px]">
            lib/payment-fees.ts
          </code>
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
              <th className="px-4 py-3 font-medium">Método</th>
              <th className="px-4 py-3 text-right font-medium">Tasa</th>
              <th className="px-4 py-3 text-right font-medium">Operaciones</th>
              <th className="px-4 py-3 text-right font-medium">Bruto</th>
              <th className="px-4 py-3 text-right font-medium">Comisión est.</th>
              <th className="px-4 py-3 text-right font-medium">Neto</th>
            </tr>
          </thead>
          <tbody>
            {METHOD_ORDER.map((m) => {
              const b = totals.byMethod[m] ?? { gross: 0, commission_est: 0, net: 0, count: 0 }
              const fee = fees.find((f) => f.method === m)
              return (
                <tr key={m} className="border-t border-ink-100">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block size-3 rounded-full"
                        style={{ background: METHOD_COLORS[m] ?? METHOD_COLORS.otro }}
                      />
                      <span className="font-medium text-ink-900">
                        {METHOD_LABELS[m] ?? m}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-ink-600">
                    {fee
                      ? `${(fee.percent * 100).toFixed(2)}%${fee.flat > 0 ? ` + ${formatMXN(fee.flat)}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-ink-700">
                    {b.count.toLocaleString('es-MX')}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-ink-900">
                    {formatMXN(b.gross)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber-700">
                    {formatMXN(b.commission_est)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                    {formatMXN(b.net)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ReporteCierre
